import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {LayoutConfigService, SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {LayoutUtilsService} from '../../../../core/_base/crud';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {AllAssetClassTypesRequested, AssetClassType, CeilingUpdated, selectAllAssetClassTypes} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';
import { HotelAssetClassTypeID } from 'src/app/core/v3/asset-class/hotel';


import {
    Ceiling,
    CeilingOnServerCreated,
    selectCeilingsActionLoading,
    selectCeilingById,
    selectLastCreatedCeilingId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-ceiling-edit',
    templateUrl: './ceiling-edit.component.html',
    styleUrls: ['./ceiling-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})


export class CeilingEditComponent implements OnInit, OnDestroy {

    model: Ceiling;
    oldModel: Ceiling;

    ceilingForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    propertySubTypes: AssetClassType[] = [];
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param layoutUtilsService: LayoutUtilsService
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private layoutUtilsService: LayoutUtilsService,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private layoutConfigService: LayoutConfigService,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectCeilingsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectCeilingById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initCeiling();
                    }
                });
            } else {
                this.model = new Ceiling();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initCeiling();
            }
        });
        this.subscriptions.push(routeSubscription);

        this.store.dispatch(new AllAssetClassTypesRequested())
        this.store.select(selectAllAssetClassTypes).subscribe(res => {
          this.propertySubTypes = [];
          if (res) {
            this.propertySubTypes = res.filter(item => item.id == 7 || item.id == 11 || item.id == 17 || item.id == 5 || item.id == 1 || item.id == 3 || item.id == HotelAssetClassTypeID);
          }
        })
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initCeiling() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.ceilingForm = this.fb.group({
            name: [this.model.name, Validators.required],
            property_sub_type_id: [this.model.property_sub_type_id, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Ceiling '${
                this.model.name
                }'`;
        }

        return 'New Ceiling';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.ceilingForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.ceilingForm.markAsPristine();
        this.ceilingForm.markAsUntouched();
        this.ceilingForm.updateValueAndValidity();
    }

    prepareCeiling(): Ceiling {
        const controls = this.ceilingForm.controls;
        const _model = new Ceiling();
        _model.clear();
        _model.id = this.model.id;
        _model.name = controls.name.value;
        _model.property_sub_type_id = controls.property_sub_type_id.value;
        _model.is_hidden = this.model ? this.model.is_hidden : false

        return _model;
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.ceilingForm.controls;
        /** check form */
        if (this.ceilingForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        const editedFacility = this.prepareCeiling();
        if (this.model.id > 0) {
            this.updateCeiling(editedFacility);
        } else {
            this.createCeiling(editedFacility);
        }
    }

    /**
     * Update model
     *
     * @param _model: CeilingModel
     */
    updateCeiling(_model: Ceiling) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateCeiling: Update<Ceiling> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new CeilingUpdated({
            partialCeiling: updateCeiling,
            ceiling: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: CeilingModel
     */
    createCeiling(_model: Ceiling) {
        this.store.dispatch(new CeilingOnServerCreated({ceiling: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedCeilingId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 &&
            this.ceilingForm && this.ceilingForm.controls.property_sub_type_id.value);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
